// src/utils/languageUtils.ts
import { Language } from '../locales/translations';

const SUPPORTED_LANGUAGES: Language[] = ['en', 'es', 'fr', 'de', 'pt'];

/**
 * Map browser language tag (e.g. "es-MX", "fr-BE") to a supported Language code.
 * Falls back to 'en' if no match found.
 */
export function detectBrowserLanguage(): Language {
  const browserLang = (navigator.language || '').toLowerCase();
  // Exact match first (e.g. "en", "de")
  if (SUPPORTED_LANGUAGES.includes(browserLang as Language)) {
    return browserLang as Language;
  }
  // Prefix match (e.g. "es-MX" → "es", "fr-CA" → "fr")
  const prefix = browserLang.split('-')[0] as Language;
  if (SUPPORTED_LANGUAGES.includes(prefix)) {
    return prefix;
  }
  return 'en';
}

/**
 * Language display names mapping
 */
export const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
};

/**
 * Get the display name for a language code
 * @param language - The language code
 * @returns The human-readable language name
 *
 * @example
 * ```typescript
 * const displayName = getLanguageDisplayName('en'); // Returns "English"
 * const displayName = getLanguageDisplayName('es'); // Returns "Español"
 * ```
 */
export function getLanguageDisplayName(language: Language): string {
  return languageNames[language] || language;
}

/**
 * Get all available languages with their display names
 * @returns An array of objects containing language codes and display names
 *
 * @example
 * ```typescript
 * const languages = getAvailableLanguages();
 * // Returns: [
 * //   { code: 'en', name: 'English' },
 * //   { code: 'es', name: 'Español' },
 * //   ...
 * // ]
 * ```
 */
export function getAvailableLanguages(): Array<{ code: Language; name: string }> {
  return Object.entries(languageNames).map(([code, name]) => ({
    code: code as Language,
    name,
  }));
}
